import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A white screen must never happen again.
 *
 * Anything that throws while a screen is drawing used to unmount the whole app
 * and leave a blank page with no words and no button. This catches it and says
 * what to do, in both languages at once (Spanish first, English beside it),
 * because the words cannot depend on the language provider: the provider may be
 * the thing that broke.
 *
 * Two buttons. "Try again" draws the screen once more, which is enough for a
 * hiccup. "Start over" clears the two things that go stale between deploys, the
 * service worker and its cached files, and reloads. It does not touch the
 * saved sign in or anything waiting to be sent, so no work is lost.
 */

export type ErrorBoundaryProps = {
  children: ReactNode;
  /** When this changes (a new route, say) a shown error clears and the screen is drawn again. */
  resetKey?: string;
};

type State = { error: Error | null };

/** Drops the service worker and its caches, then reloads from the network. */
export async function startOver(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(registrations.map((r) => r.unregister()));
  } catch {
    // Nothing to unregister, or the browser will not say. Reload anyway.
  }
  try {
    const names = (await globalThis.caches?.keys?.()) ?? [];
    await Promise.all(names.map((name) => globalThis.caches.delete(name)));
  } catch {
    // Same: reload anyway.
  }
  window.location.assign('/');
}

const BUTTON =
  'min-h-tap w-full rounded border border-line px-4 py-3 text-center text-body font-semibold ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';

export class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where a person debugging on a phone will look.
    console.error('Screen crashed:', error, info.componentStack);
  }

  override componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div role="alert" className="min-h-dvh bg-ground px-4 py-8 text-ink font-body text-body">
        <div className="mx-auto flex max-w-reading flex-col gap-5">
          <h1 className="font-display text-[1.6em] leading-tight">
            <span lang="es">Algo salió mal en esta pantalla.</span>
            <br />
            <span lang="en" className="text-inkMuted">
              Something went wrong on this screen.
            </span>
          </h1>
          <p>
            <span lang="es">Toca “Intentar de nuevo”. Si sigue igual, toca “Empezar de nuevo”. No pierdes nada.</span>
            <br />
            <span lang="en" className="text-inkMuted">
              Tap “Try again”. If it stays the same, tap “Start over”. Nothing is lost.
            </span>
          </p>
          <button type="button" className={`${BUTTON} bg-accent text-onAccent`} onClick={() => this.setState({ error: null })}>
            Intentar de nuevo / Try again
          </button>
          <button type="button" className={`${BUTTON} bg-surface text-ink`} onClick={() => void startOver()}>
            Empezar de nuevo / Start over
          </button>
          <details className="text-inkMuted">
            <summary className="min-h-tap cursor-pointer py-2">Detalle / Details</summary>
            <pre className="whitespace-pre-wrap break-words font-mono text-caption">{String(error.message || error)}</pre>
          </details>
        </div>
      </div>
    );
  }
}
